import { Injectable } from '@angular/core';
import { CrudApiProvider } from '../crud-api/crud-api';
import { UserProvider } from '../user/user';
import { EventEmitter } from '../../includes/eventEmitter';
import { AuthProvider } from '../auth/auth';
import * as _ from 'lodash';
import * as debug from 'debug';

@Injectable()
export class JobAdsProvider extends EventEmitter {
  log = debug('app:JobAdsProvider');

  lastQueryJSON;
  MINIMUM_BEFORE_FALLBACK = 5;

  jobs: any[];

  constructor(
    public crudApi: CrudApiProvider,
    public auth: AuthProvider,
    public user: UserProvider
  ) {
    super();
    this.log('constructor');

    // For now, we have turned off job ads loading this way.
    // we will now use the ad delivery network

    this.user.on('updated', () => {
      this.log('user updated');
    });

    // reload when authenticated
    this.user.on('appliedForJob', (job, poster) => {
      this.jobs = this.filterJobs(this.jobs);
      this.log('filtered', _.size(this.jobs));
    });
  }

  private filterJobs(jobs) {
    const totalCount = _.size(jobs),
      // the goal here is to make sure that no company takes up more than
      // 20% of jobs
      percent = 0.2,
      countByCompany: any = {},
      // dont' want to mess with this if it's so small
      maxCountByCompany = Math.max(totalCount * percent, 4);

    this.log('max jobs per any company', maxCountByCompany);

    const result = _.filter(jobs, j => {
      const company = _.get(j, 'jobInformation.poster._id'),
        hasAppliedToCompany = this.user.hasAppliedToCompany(company);
      if (hasAppliedToCompany) {
        return false;
      } else {
        countByCompany[company] = (countByCompany[company] || 0) + 1;
        if (countByCompany[company] >= maxCountByCompany) {
          console.log('stripping company', company, countByCompany[company]);
          return false;
        } else {
          return true;
        }
      }
    });
    return result;
  }

  private loadJobs(): Promise<any> {
    let zip = this.user.get('jobSearchInfo.zip'),
      query = _.merge(
        {
          postType: 'JOB',
          sortBy: 'score.random:desc',
          limit: 100,
        },
        this.user.filterAppliedCompanies(),
        this.user.getFilteredJobQuery()
      ),
      queryJson = JSON.stringify(query);
    this.log('will load job ads', this.user.get());

    if (queryJson === this.lastQueryJSON && _.size(this.jobs)) {
      this.log('no change');
      return Promise.resolve(this.jobs || []);
    }

    this.lastQueryJSON = queryJson;

    return this.crudApi
      .get('/api/posts', query)
      .then(res => {
        return Promise.resolve(this.filterJobs(res.data));
      })
      .then(data => {
        if (_.size(data) > this.MINIMUM_BEFORE_FALLBACK) {
          return Promise.resolve(data);
        } else {
          return this.loadRandom();
        }
      })
      .then(
        data => {
          let jobs = this.shuffleJobs(_.clone(data));
          this.log('loaded jobs', jobs);
          this.jobs = jobs;
          this.emit('loaded');
          return jobs;
        },
        e => {}
      );
  }

  private jobList(): Promise<any> {
    if (_.size(this.jobs)) {
      return Promise.resolve(this.jobs);
    } else {
      return this.loadJobs();
    }
  }

  // retrieve n jobs
  getJobs(n) {
    return this.jobList().then(jobs => {
      let list = this.shuffleJobs(jobs.slice());
      return Promise.resolve(list.slice(0, n));
    });
  }

  private shuffleJobs(jobs) {
    jobs = _.slice(jobs);
    const returnValue = [];
    let lastCompany;

    _.each(_.range(jobs.length), _i => {
      if (!jobs.length) {
        return;
      } // just in case?

      let idx,
        company,
        job,
        attempts = 0;
      do {
        idx = _.random(0, jobs.length - 1, false);
        job = jobs[idx];
        company = _.get(job, 'jobInformation.poster._id');
        attempts++;
      } while (company === lastCompany && attempts < 5);

      if (job) {
        returnValue.push(job);
        lastCompany = company;
        jobs.splice(idx, 1);
      }
    });

    this.log('shuffled jobs', returnValue);
    return returnValue;
  }

  private loadRandom() {
    this.log('loading random');
    return this.crudApi
      .get('/api/posts', {
        postType: 'JOB',
        sortBy: 'score.random:desc',
        limit: 100,
      })
      .then(res => {
        return Promise.resolve(this.filterJobs(res.data));
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/job-ads/job-ads.ts